import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
} from "@mui/material";
import { useEffect, useState, useCallback, useContext } from "react";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import API_URLS from "../../../constants/apiConstants";
import { BiX } from "react-icons/bi";
import "../css/RoomModal.css";
import AuthContext from "../../../context/AuthProvider";
import { useTranslation } from "react-i18next";

export default function ParentModal({
  open,
  onClose,
  data = {},
  onParentSaved,
  mode = "add",
}) {
  const axiosPrivate = useAxiosPrivate();
  const { school } = useContext(AuthContext);
  console.log("data", data);
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  const [form, setForm] = useState({
   firstName: "", 
    lastName: "",
    email: "",
    phone: "",
    address: "",
    notes: "",
  });

  const [selectFields, setSelectFields] = useState({});
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const resetForm = useCallback(() => {
    setForm({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      notes: "",
    });
    setSelectFields({});
    setErrors({});
  }, []);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSelectChange = (fieldKey) => (e) => {
    setSelectFields((prev) => ({ ...prev, [fieldKey]: e.target.value }));
  };

  const validateForm = () => {
    const newErrors = {};

   if (!form.firstName.trim()) {
      newErrors.firstName = t("parent_modal.errors.firstNameRequired");
    }
    if (!form.lastName.trim()) {
      newErrors.lastName = t("parent_modal.errors.lastNameRequired");
    }
    if (!form.email.trim()) {
      newErrors.email = t("parent_modal.errors.emailRequired");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const payload = {
        FirstName: form.firstName.trim(),
        LastName: form.lastName.trim(),
        email: form.email.trim(),
        Phone: form.phone.trim(),
        Address: form.address.trim(),
        Notes: form.notes.trim(),
        Nursery: school.id,
        ...selectFields,
      };

      let res;
      if (mode === "edit" && data?.parent?.id) {
        res = await axiosPrivate.put(
          `${API_URLS.parent}/${data.parent.id}`,
          payload
        );
        onParentSaved?.(data.parent.id, payload);
      } else {
        res = await axiosPrivate.post(API_URLS.parent, payload);
        onParentSaved?.(res.data.id, payload);
      }

      handleClose();
    } catch (error) {
      if (
        error.response?.status === 400 &&
        typeof error.response.data === "object"
      ) {
        const fieldErrors = {};
        for (const key in error.response.data) {
          if (Array.isArray(error.response.data[key])) {
            fieldErrors[key.toLowerCase()] = error.response.data[key][0];
          }
        }
        setErrors((prev) => ({ ...prev, ...fieldErrors }));
      } else {
        setErrors((prev) => ({
          ...prev,
          server: t("parent_modal.errors.generic"),
        }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Prefill data in edit mode
  useEffect(() => {
    if (open) {
      if (mode === "edit" && data?.parent?.id) {
        setIsLoading(true);

        axiosPrivate
          .get(`${API_URLS.parent}/${data.parent.id}`)
          .then((res) => {
            const parent = res.data;
            setForm({
              firstName: parent.FirstName || "",
              lastName: parent.LastName || "",
              email: parent.email || "",
              phone: parent.Phone || "",
              address: parent.Address || "",
              notes: parent.Notes || "",
            });

            if (Array.isArray(data.fields)) {
              const initialSelects = {};
              data.fields.forEach((f) => {
                initialSelects[f.key] = f.value || "";
              });
              setSelectFields(initialSelects);
            }
          })
          .catch((err) => {
            console.error("Error fetching parent details:", err);
            setErrors((prev) => ({
              ...prev,
              server: t("parent_modal.errors.generic"),
            }));
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        resetForm();
      }
    }
  }, [open, mode, data, resetForm, axiosPrivate, t]);

  const selectFieldData =
    mode === "edit" ? data.fields || [] : Array.isArray(data) ? data : [];

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className="modalStyle">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <Typography variant="h5" fontWeight="bold" color="secondary">
            {mode === "edit"
              ? t("parent_modal.editTitle")
              : t("parent_modal.title")}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <BiX />
          </IconButton>
        </Box>

        {[
          { label: "First Name", field: "firstName", type: "text", required: true },
          { label: "Last Name", field: "lastName", type: "text", required: true },
          { label: "Email", field: "email", type: "email", required: true },
          { label: "Phone Number", field: "phone", type: "text" },
          { label: "Address", field: "address", type: "text" },
        ].map(({ label, field, type, required }) => (
          <Box mb={3} key={field}>
            <Typography variant="body1" color="secondary" gutterBottom>
              {t(`parent_modal.fields.${field}`)}{" "}
              {required && <span className="text-danger">*</span>}
            </Typography>
            <input
              type={type}
              value={form[field]}
              onChange={handleChange(field)}
              className={`form-control ${errors[field] ? "is-invalid" : ""}`}
              placeholder={t(`parent_modal.placeholders.${field}`)}
              disabled={isLoading}
            />
            {errors[field] && (
              <Typography className="errorText">{errors[field]}</Typography>
            )}
          </Box>
        ))}

        {selectFieldData.map((field) => (
          <Box mb={3} key={field.label}>
            <Typography variant="body1" color="secondary" gutterBottom>
             {t(`parent_modal.fields.${field.label.toLowerCase()}`, { defaultValue: field.label })}
            </Typography>
            <Select
              fullWidth
              displayEmpty
              value={selectFields[field.key] || ""}
              onChange={handleSelectChange(field.key)}
              disabled={isLoading}
              sx={{
                height: "40px",
                "& .MuiSelect-select": {
                  paddingTop: "8px",
                  paddingBottom: "8px",
                },
              }}
            >
              <MenuItem value="" disabled>
                --- {t("actions.select")} ---
              </MenuItem>
              {field.options.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                {field.key === "countries" 
                  ? t(`countries.${opt.id}`, { defaultValue: opt.name }) 
                  : 
                  field.key === "Nationality" || field.label === "Nationality"
                  ? (i18n.language === "ar" ? (opt.Name_ar || opt.name) : opt.name)
                  : 
                  t(`options.${opt.name}`, { defaultValue: opt.name })
                }
              </MenuItem>
            ))}
            </Select>
          </Box>
        ))}

        <Box mb={3}>
          <Typography variant="body1" color="secondary" gutterBottom>
            {t("parent_modal.fields.notes")}
          </Typography>
          <textarea
            rows={4}
            value={form.notes}
            onChange={handleChange("notes")}
            className="form-control"
            placeholder={t("parent_modal.placeholders.notes")}
            disabled={isLoading}
            style={{
              width: "100%",
              padding: "8px 12px",
              borderRadius: "4px",
              border: "1px solid #ced4da",
              fontFamily: "inherit",
              fontSize: "inherit",
              minHeight: "100px",
            }}
          />
        </Box>

        {errors.server && (
          <Typography className="errorText" mb={2}>
            {errors.server}
          </Typography>
        )}

        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isLoading}
            startIcon={
              isLoading ? <CircularProgress size={20} color="inherit" /> : null
            }
            className="submitButton"
          >
            {isLoading
              ? t("room_modal.processing")
              : mode === "edit"
              ? t("actions.edit")
              : t("actions.add")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
